import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function formatDate(date: Date): string {
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  }).format(date);
}

export function daysUntilExpiry(expiryDate: string): number {
  const today = new Date();
  const expiry = new Date(expiryDate);
  const diffTime = expiry.getTime() - today.getTime();
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
}

export function getExpiryColor(days: number): string {
  if (days < 0) return 'bg-destructive/20 text-destructive border border-destructive/30';
  if (days < 3) return 'bg-orange-500/15 text-orange-600 dark:text-orange-400 border border-orange-500/20';
  if (days < 7) return 'bg-amber-500/15 text-amber-600 dark:text-amber-400 border border-amber-500/20';
  return 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 border border-emerald-500/20';
}

export function getRandomInt(min: number, max: number): number {
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

// Pastel, less vibrant color mapping for categories
const CATEGORY_COLOR_MAP: Record<string, string> = {
  vegetable: 'bg-green-50', // pastel green
  fruit: 'bg-rose-50',     // pastel red/pink
  meat: 'bg-rose-50',       // pastel pink/red
  dairy: 'bg-yellow-50',   // pastel yellow
  grain: 'bg-amber-50',    // pastel beige/brown
  seafood: 'bg-cyan-50',    // pastel cyan
  frozen: 'bg-indigo-50',   // pastel indigo
  snack: 'bg-orange-50',    // pastel orange
  beverage: 'bg-teal-50',   // pastel teal
  other: 'bg-gray-50',      // pastel gray
};

// Utility to normalize category keys (singularize common plurals)
function normalizeCategoryKey(category: string): string {
  const key = category.trim().toLowerCase();
  if (key.endsWith('ies')) return key.replace(/ies$/, 'y'); // e.g., 'dairies' -> 'dairy'
  if (key.endsWith('es')) return key.replace(/es$/, 'e'); // e.g., 'vegetables' -> 'vegetable'
  if (key.endsWith('s')) return key.slice(0, -1); // e.g., 'fruits' -> 'fruit', 'grains' -> 'grain'
  return key;
}

// Utility to get a pastel color for a category, with fallback for unknowns
export function getCategoryColor(category: string): string {
  if (!category) return CATEGORY_COLOR_MAP.other;
  const key = normalizeCategoryKey(category);
  if (CATEGORY_COLOR_MAP[key]) return CATEGORY_COLOR_MAP[key];
  // Fallback: generate a pastel color based on hash
  const pastelColors = [
    'bg-green-50', 'bg-rose-50', 'bg-yellow-50', 'bg-amber-50',
    'bg-cyan-50', 'bg-indigo-50', 'bg-orange-50', 'bg-teal-50', 'bg-gray-50'
  ];
  let hash = 0;
  for (let i = 0; i < key.length; i++) {
    hash = key.charCodeAt(i) + ((hash << 5) - hash);
  }
  const idx = Math.abs(hash) % pastelColors.length;
  return pastelColors[idx];
}

// PWA utility functions
export const isPWAStandalone = (): boolean => {
  // Check if running as standalone PWA
  return Boolean(
    (window.navigator as any).standalone ||  // iOS Safari
    window.matchMedia('(display-mode: standalone)').matches ||  // Standard PWA
    window.matchMedia('(display-mode: fullscreen)').matches     // Fullscreen PWA
  );
};

export const getPWAInfo = () => {
  const isStandalone = isPWAStandalone();
  const isInBrowser = !isStandalone;
  const userAgent = navigator.userAgent;
  const isIOS = /iPad|iPhone|iPod/.test(userAgent);
  const isSafari = /Safari/.test(userAgent) && !/Chrome/.test(userAgent);
  
  return {
    isStandalone,
    isInBrowser,
    isIOS,
    isSafari,
    userAgent: userAgent.substring(0, 50) + '...', // Truncated for logging
  };
};

/**
 * UNIFIED NUTRITION NUMBER FORMATTING UTILITIES
 * Ensures consistent number display across all nutrition components
 */

// Format calories with no decimal places (always whole numbers)
export const formatCalories = (value: number): number => {
  return Math.round(value);
};

// Format macronutrients (protein, carbs, fat, fiber) with max 1 decimal place
export const formatMacronutrient = (value: number): number => {
  return Math.round(value * 10) / 10;
};

// Format micronutrients (sodium, potassium) as whole numbers
export const formatMicronutrient = (value: number): number => {
  return Math.round(value);
};

// Format percentages with no decimal places
export const formatPercentage = (value: number): number => {
  return Math.round(value);
};

// Format weight values with 1 decimal place
export const formatWeight = (value: number): number => {
  return Math.round(value * 10) / 10;
};

// Format general nutrition display values
export const formatNutritionValue = (value: number, type: 'calories' | 'macro' | 'micro' | 'percentage' | 'weight' = 'macro'): number => {
  switch (type) {
    case 'calories':
      return formatCalories(value);
    case 'macro':
      return formatMacronutrient(value);
    case 'micro':
      return formatMicronutrient(value);
    case 'percentage':
      return formatPercentage(value);
    case 'weight':
      return formatWeight(value);
    default:
      return formatMacronutrient(value);
  }
};

// Format remaining calories display (handles positive/negative values)
export const formatRemainingCalories = (remaining: number): string => {
  const formatted = formatCalories(Math.abs(remaining));
  return remaining >= 0 ? `${formatted} calories remaining` : `${formatted} calories over goal`;
};